import { styled } from '@mui/material';
import { Fragment, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import { getCanonicalLocale, getI18nextLocale, joinSeoPath } from '../../Common/SEO/SeoLinks';
import { getSeoProductsName } from '../../Common/SEO/StructuredData';
import { getLocalizedViewCategorySlug, getProductCategoryPath } from './productBreadcrumbData';

interface ProductBreadcrumbsProps {
  viewProductId?: number
}

interface ProductBreadcrumbItem {
  label: string
  to: string
}

const BreadcrumbNav = styled('nav')(({ theme }) => ({
  alignItems: 'center',
  color: 'rgba(0, 0, 0, 0.58)',
  display: 'flex',
  flexWrap: 'wrap',
  fontFamily: 'Poppins, Roboto, sans-serif',
  fontSize: 12,
  fontWeight: 600,
  gap: theme.spacing(0.75),
  letterSpacing: 0,
  lineHeight: 1.4,
  marginBottom: theme.spacing(1.25),
  maxWidth: '100%',

  '& a': {
    color: 'inherit',
    textDecoration: 'none',
    textUnderlineOffset: 4,
    transition: 'color 160ms ease, opacity 160ms ease',
  },

  '& a:hover, & a:focus-visible': {
    color: 'rgba(0, 0, 0, 0.86)',
    outline: 'none',
    textDecoration: 'underline',
  },

  '& .product-breadcrumb-separator': {
    color: 'rgba(0, 0, 0, 0.28)',
    userSelect: 'none',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: 11,
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
}));

function ProductBreadcrumbs(props: ProductBreadcrumbsProps) {
  const [, routesI18n] = useTranslation('routes');
  const viewProductData = useAppSelector(state => (
    props.viewProductId !== undefined
      ? state.get('appData').get('viewProducts').get(String(props.viewProductId))
      : undefined
  ));
  const viewCategories = useAppSelector(state => state.get('appData').get('viewCategories'));
  const currentLocale = getCanonicalLocale(routesI18n.resolvedLanguage ?? routesI18n.language);

  const breadcrumbItems = useMemo<ProductBreadcrumbItem[]>(() => {
    if (viewProductData === undefined) return [];

    const fixedRoutes = routesI18n.getFixedT(getI18nextLocale(currentLocale), 'routes');
    const productBasePath = fixedRoutes('/products');
    const items: ProductBreadcrumbItem[] = [{
      label: getSeoProductsName(currentLocale),
      to: productBasePath,
    }];
    const categoryPath: string[] = [];

    getProductCategoryPath(viewProductData, viewCategories).forEach(category => {
      categoryPath.push(getLocalizedViewCategorySlug(category, currentLocale));
      items.push({
        label: category.get('name'),
        to: joinSeoPath(productBasePath, ...categoryPath),
      });
    });

    return items;
  }, [currentLocale, routesI18n, viewCategories, viewProductData]);
  const breadcrumbAriaLabel = useMemo(() => {
    return breadcrumbItems.map(item => item.label).join(' / ');
  }, [breadcrumbItems]);
  const renderBreadcrumbItem = useCallback((item: ProductBreadcrumbItem, index: number) => {
    return <Fragment key={item.to}>
      {index > 0 ? <span className="product-breadcrumb-separator" aria-hidden="true">/</span> : null}
      <Link to={item.to}>{item.label}</Link>
    </Fragment>;
  }, []);

  if (breadcrumbItems.length === 0) {
    return null;
  }

  return <BreadcrumbNav aria-label={breadcrumbAriaLabel}>
    {breadcrumbItems.map(renderBreadcrumbItem)}
  </BreadcrumbNav>;
}

export default memo(ProductBreadcrumbs);
